import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { ITopFrequentlyUsedBooks } from "../api/types";

// Single sequential hue - this feature only ever shows rankings
// (magnitude comparisons), never part-of-whole shares, so one hue
// across every bar chart keeps the whole page reading as one system.
const CHART_COLOR = "#5B8FF9";

const MAX_LABEL_LENGTH = 28;

interface TopBooksChartProps {
  data: ITopFrequentlyUsedBooks[] | undefined;
  height?: number;
  topN?: number;
}

const TopBooksChart = ({ data, height = 380, topN = 10 }: TopBooksChartProps) => {
  const chartData = useMemo(
    () =>
      [...(data ?? [])]
        .sort((a, b) => (b.totalIssueCount ?? 0) - (a.totalIssueCount ?? 0))
        .slice(0, topN)
        .map((item) => ({
          title:
            item.title && item.title.length > MAX_LABEL_LENGTH
              ? `${item.title.slice(0, MAX_LABEL_LENGTH)}…`
              : item.title,
          fullTitle: item.title,
          totalIssueCount: item.totalIssueCount,
        }))
        .reverse(),
    [data, topN],
  );

  if (!chartData.length) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="title"
            width={170}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value: number) => [value, "Total Issue Count"]}
            labelFormatter={(_label, payload) =>
              payload?.[0]?.payload?.fullTitle ?? _label
            }
          />
          <Bar dataKey="totalIssueCount" fill={CHART_COLOR} barSize={18}>
            <LabelList dataKey="totalIssueCount" position="right" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TopBooksChart;
